import {FilterIcon, SearchIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, CounterLabel, IconButton, TextInput} from '@primer/react'
import {clsx} from 'clsx'
import styles from './CommentsFilter.module.css'

import {GitHubAvatar} from '@github-ui/github-avatar'
import {FeatureFlags} from '@primer/react/experimental'
import type {AuthorFilterItem} from './utils/get-author-filters'
import type {CommentsFilterState} from './use-comments-filter-state'

interface CommentsFilterProps {
  authorList: AuthorFilterItem[]
  className?: string
  filterState: CommentsFilterState
  onFilterStateChange: (filterState: CommentsFilterState) => void
  hideReviewThreadFilters: boolean
}

export function CommentsFilter({
  authorList,
  className,
  filterState,
  onFilterStateChange,
  hideReviewThreadFilters,
}: CommentsFilterProps) {
  const updateFilterText = (filterText: string) => {
    onFilterStateChange({...filterState, filterText})
  }

  const toggleResolvedFilter = () => {
    onFilterStateChange({...filterState, hideResolvedThreads: !filterState.hideResolvedThreads})
  }

  const toggleOutdatedFilter = () => {
    onFilterStateChange({...filterState, hideOutdatedThreads: !filterState.hideOutdatedThreads})
  }

  const onSelectAuthor = (authorLogin: string) => {
    if (filterState.selectedAuthor === authorLogin) {
      return onFilterStateChange({...filterState, selectedAuthor: undefined})
    }

    onFilterStateChange({...filterState, selectedAuthor: authorLogin})
  }

  const hasTextFilter = filterState.filterText.trim().length > 0
  const hasAuthorFilter = !!filterState.selectedAuthor
  const hasReviewFilter = filterState.hideResolvedThreads || filterState.hideOutdatedThreads

  // The conversation panel doesn't expose resolved/outdated toggles, so those values
  // remain at their defaults and should not trigger the indicator.
  const isFilteringActive = hasTextFilter || hasAuthorFilter || (!hideReviewThreadFilters && hasReviewFilter)

  return (
    <div className={clsx('d-flex flex-row gap-2 flex-nowrap', className)}>
      <TextInput
        block
        aria-label="Filter comments"
        leadingVisual={SearchIcon}
        placeholder="Filter comments"
        value={filterState.filterText}
        onChange={event => updateFilterText(event.target.value)}
      />

      <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
        <div className="position-relative">
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton
                icon={FilterIcon}
                aria-label={
                  isFilteringActive ? 'Additional comment filters (filters applied)' : 'Additional comment filters'
                }
                description="Additional comment filters"
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="small">
              <ActionList>
                {!hideReviewThreadFilters && (
                  <ActionList.Group selectionVariant="multiple">
                    <ActionList.Item
                      selected={!filterState.hideResolvedThreads}
                      onSelect={() => toggleResolvedFilter()}
                    >
                      Show resolved comments
                    </ActionList.Item>
                    <ActionList.Item
                      selected={!filterState.hideOutdatedThreads}
                      onSelect={() => toggleOutdatedFilter()}
                    >
                      Show outdated comments
                    </ActionList.Item>
                    <ActionList.Divider />
                  </ActionList.Group>
                )}
                <ActionList.Group selectionVariant="single">
                  <ActionList.GroupHeading>Filter by author</ActionList.GroupHeading>

                  {authorList.map(author => (
                    <ActionList.Item
                      key={author.login}
                      selected={filterState.selectedAuthor === author.login}
                      onSelect={() => onSelectAuthor(author.login)}
                    >
                      <ActionList.LeadingVisual>
                        <GitHubAvatar src={author.avatarUrl} />
                      </ActionList.LeadingVisual>
                      {author.login}
                      <ActionList.TrailingVisual>
                        <CounterLabel>{author.count}</CounterLabel>
                      </ActionList.TrailingVisual>
                    </ActionList.Item>
                  ))}
                </ActionList.Group>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
          {isFilteringActive && <span className={styles.filterIndicator} aria-hidden="true" />}
        </div>
      </FeatureFlags>
    </div>
  )
}
